import { BaseSystem, SystemOperationDefinition, Zed } from '@modules/core';
import { PricePolicy } from '@zigbang/utils';
import { World } from 'miniplex';

import {
  Entity as BaseEntity,
  DanjiEntity,
  DanjiUnitPresalePrice,
  DanjiUnitPresalePriceData,
  DanjiUnitPrice,
  DanjiUnitPriceData,
  DanjiUnitPublicPrice,
  DanjiUnitPublicPriceData,
  DongEntity,
  DongSystem,
  getDanjiUnitPresalePrice,
  getDanjiUnitPrice,
  ServiceType,
} from '../..';

import { UnitEntity, UnitSystem } from './UnitSystem';

import type { Promisable } from 'type-fest';

export type UnitPrice = {
  formattedPrice?: string;
  price?: number | undefined;
  zigbangPrice?: DanjiUnitPrice;
  publicPrices?: DanjiUnitPublicPrice;
  presalePrices?: DanjiUnitPresalePrice;
};

export type UnitPriceData = Partial<DanjiUnitPriceData & DanjiUnitPublicPriceData & DanjiUnitPresalePriceData>;

export type UnitPriceEntity = {
  UnitPrice?: UnitPrice;
  UnitPriceDataPending?: Promise<UnitPriceData>;
  UnitPriceData?: UnitPriceData;
};

type Entity = UnitPriceEntity & UnitEntity & DanjiEntity & DongEntity & BaseEntity;

export class UnitPriceSystem implements BaseSystem {
  static readonly INJECTABLE_TOKEN = Symbol.for('UnitPriceSystem');

  private readonly world;
  private readonly withDanjiData;
  private readonly withDongInit;
  private readonly withUnitPriceData;
  private readonly withoutUnitPriceData;
  private readonly withoutUnitPrice;

  private readonly unitPrices = new Map<number, UnitPrice>();
  private readonly needUnitPrice = new Map<number, Entity>();

  private _showPresalePrice = false;
  get showPresalePrice() {
    return this._showPresalePrice;
  }

  constructor({
    [Zed.WORLD_TOKEN]: world,
    [DongSystem.INJECTABLE_TOKEN]: dongSystem,
    [UnitSystem.INJECTABLE_TOKEN]: unitSystem,
  }: {
    [Zed.WORLD_TOKEN]: World<Entity>;
    [DongSystem.INJECTABLE_TOKEN]: DongSystem;
    [UnitSystem.INJECTABLE_TOKEN]: UnitSystem;
  }) {
    this.world = world;
    this.withDanjiData = world.with('DanjiData');
    this.withDongInit = world.with('DongInit', 'DongData');
    this.withoutUnitPriceData = this.withDongInit.without('UnitPriceDataPending', 'UnitPriceData');
    this.withUnitPriceData = world.with('UnitPriceData');
    this.withoutUnitPrice = world.with('UnitInit', 'UnitData').without('UnitPrice');

    this.operations = [
      {
        group: 'beforeRender',
        run: this.populateUnitPriceData,
        after: [dongSystem['createDongInit']],
      },
      {
        group: 'beforeRender',
        run: this.populateUnitPrice,
        after: [this.populateUnitPriceData, unitSystem['createUnitInit']],
      },
    ];
  }

  private populateUnitPriceData = () => {
    const danjiData = this.withDanjiData.first?.DanjiData;
    if (!danjiData) {
      return;
    }

    // when all unit price data is ready, set rest of unit price data to null
    if (this.withUnitPriceData.size === danjiData.dongs.length && this.needUnitPrice.size !== 0) {
      this.needUnitPrice.forEach((entity, key) => {
        const prices = this.unitPrices.get(key);
        this.updateUnitPrice(entity, prices ?? {});
        this.needUnitPrice.delete(key);
      });
      return;
    }

    const showPresalePrice = shouldShowPresalePrice(danjiData.서비스구분);
    this._showPresalePrice = showPresalePrice;

    for (const entity of this.withoutUnitPriceData) {
      const { DongData } = entity;
      const price: Promise<UnitPriceData> = showPresalePrice
        ? getDanjiUnitPresalePrice(DongData.areaBuildingId)
        : getDanjiUnitPrice(DongData.areaBuildingId);

      this.world.update(entity, 'UnitPriceDataPending', price);

      price.then((price) => {
        Object.entries(price).forEach(([type, prices]) => {
          prices.forEach((price) => {
            const unitId = 'areahoId' in price ? price.areahoId : price.areaHoId;
            const existingPrice = this.unitPrices.get(unitId);
            const prices = {
              ...existingPrice,
              [type]: price,
            };
            this.unitPrices.set(unitId, prices);

            const needUnitPrice = this.needUnitPrice.get(unitId);
            if (needUnitPrice) {
              this.updateUnitPrice(needUnitPrice, prices);
              this.needUnitPrice.delete(unitId);
            }
          });
        });
        this.world.update(entity, 'UnitPriceData', price);
        this.world.removeComponent(entity, 'UnitPriceDataPending');
      });
    }
  };

  private populateUnitPrice = () => {
    for (const entity of this.withoutUnitPrice) {
      const { UnitData } = entity;
      const unitId = UnitData.meta.ho_id;
      const prices = this.unitPrices.get(unitId);
      if (!prices) {
        this.needUnitPrice.set(unitId, entity);
      } else {
        this.updateUnitPrice(entity, prices);
      }
    }
  };

  private updateUnitPrice = (entity: Entity, prices: UnitPrice) => {
    const showPresalePrice = this.showPresalePrice && prices.presalePrices?.price;
    const price = showPresalePrice
      ? prices.presalePrices?.price
      : prices.zigbangPrice?.trade.price ?? prices.zigbangPrice?.charter.price;
    const formattedPrice = PricePolicy.markerPricePolicy(price);
    this.world.update(entity, 'UnitPrice', {
      price,
      formattedPrice,
      ...prices,
    });
  };

  readonly operations: SystemOperationDefinition[];
}

export const shouldShowPresalePrice = (serviceType: ServiceType) =>
  ['입주예정', '청약중', '청약마감'].includes(serviceType);
